import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, Validator {

  // ── Inputs ───────────────────────────────────────────────────────────────────
  @Input() placeholder = '';
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'search' = 'text';
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorMessage = 'Ce champ est obligatoire';
  @Input() clearable = false;
  @Input() suffix    = '';

  @Output() cleared = new EventEmitter<void>(); // émis quand l'utilisateur clique sur ×

  // ── State ────────────────────────────────────────────────────────────────────
  value     = signal('');
  isFocused = signal(false);
  isTouched = signal(false);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: string) => void = () => {};
  private _onTouched: () => void          = () => {};

  writeValue(val: string): void                    { this.value.set(val ?? ''); }
  registerOnChange(fn: (v: string) => void): void  { this._onChange = fn; }
  registerOnTouched(fn: () => void): void          { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void      { this.disabled = isDisabled; }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    return this.required && !this.value() ? { required: true } : null;
  }

  // ── Events ────────────────────────────────────────────────────────────────────
  onInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value.set(val);
    this._onChange(val);
  }

  onFocus(): void { this.isFocused.set(true); }

  onBlur(): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this._onTouched();
  }

  onClear(): void {
    this.value.set('');
    this._onChange('');
    this._onTouched();
    this.cleared.emit();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  get showError(): boolean { return this.required && this.isTouched() && !this.value(); }
}